import React, {useState} from 'react';

import NoResultsMessage from 'app/components/general/NoResultsMessage';
import TabbedModal from 'app/components/general/TabbedModal';
import {DashboardItemsCounter} from 'app/general/dashboards/DashboardItemsCounter';
import {useLanes} from 'app/lanes/api';
import {SIDEBAR_PAGE_SIZE} from 'app/utils/constants';
import {gt, ngt} from 'app/utils/i18n';

import {ButtonPagination} from './ButtonPagination';
import {ManageLanesItemList} from './ManageLanesItemList';

interface Props {
  show: boolean;
  onClose: () => void;
}

export function ManageLanesModal({show, onClose}: Props): JSX.Element {
  return (
    <TabbedModal
      show={show}
      onCancel={onClose}
      scrollable={true}
      tabs={[
        {
          eventKey: 'manage-lanes',
          name: gt('Manage lanes'),
          icon: 'fas fa-road',
          content: <ManageLanesTab />,
        },
      ]}
    />
  );
}

function ManageLanesTab() {
  const [page, setPage] = useState(1);
  const {data, isError} = useLanes({page, page_size: SIDEBAR_PAGE_SIZE});

  return (
    <div className="container-fluid">
      {data == null ? (
        isError ? (
          <NoResultsMessage
            onSidebar={true}
            icon="fa-exclamation-triangle text-warning"
            head={gt('Could not load the lanes')}
          >
            {gt('Please try again later')}
          </NoResultsMessage>
        ) : (
          <div className="font-italic text-muted py-4 text-center mb-3">
            <i className="fa fa-circle-notch fa-spin mr-1" />
            {gt('Loading lanes...')}
          </div>
        )
      ) : data.results.length === 0 ? (
        <NoResultsMessage
          onSidebar={true}
          icon="fas fa-road"
          head={gt('No lanes created yet.')}
        >
          {gt(
            'Use the "%s" button to create a lane and attach it to your document steps',
            <b>{gt('Set Step Lane')}</b>
          )}
        </NoResultsMessage>
      ) : (
        <>
          <div className="row">
            <DashboardItemsCounter
              text={ngt('%s lane', '%s lanes', data.count)}
            />
          </div>
          {data.results.map((lane, index) => (
            <ManageLanesItemList
              key={lane.hashid}
              lane={lane}
              borderBottom={index == data.results.length - 1}
            />
          ))}
          <div className="mt-2">
            <ButtonPagination
              currentPage={page}
              response={data}
              onPageChange={setPage}
            />
          </div>
        </>
      )}
    </div>
  );
}
